import type {Vec2Simple} from "../../../../../Utils/ThreeJS/VectorStructs.js";
import type {PathTransform, InterpolateConfig} from "../ObjPath.js";
import {BuildCatmullRomBackbone, TessellateFbmNoise, type BackboneOptions} from "../../../../../Terrain/Heights/CurveCompletion.js";
import {rngStream, rngStreamPath} from "../../../../../Terrain/Heights/TerrainRNG.js";

/** A plain 2D vector — used instead of `Vec2` to keep this module MobX-/graphlink-free
 *  (Vec2's home module imports `AddSchema` from mobx-graphlink). */
type Normal = {x: number, y: number};

/** Seeding context for the fBm-noise interpolate step. The renderer and the solver must
 *  agree on the seed so the rendered curve and the solved plateau outline match.
 *  - `pathId`: the path's stable path_id (or the resolved origin-path-id for inherited
 *    paths whose `useSourcePathIDForSeeding` is on).
 *  - `worldSeed`: the terrain world-seed; only folded into the RNG when `useWorldSeed` is
 *    true (i.e. the path has an active terrain-feature — the former "is isoline" gate).
 *  - `useWorldSeed`: mirrors `PathTerrainFeature.IsActive(path.terrainFeature)`.
 *  - `objWorldPos`: the owning MapObject's resolved world position. Required when an fBm
 *    interpolate uses `noiseSpace = "world"` (so the noise samples at world-XY, not
 *    local-XY); unused for `"curve"` and `"local"`.
 *  Optional: when absent, fBm falls back to non-deterministic (Math.random) seeding — but
 *  all real callers (renderer + solver) supply it, so this is just a defensive default. */
export type InterpolateSeedContext = {
	pathId: string;
	worldSeed: number;
	useWorldSeed: boolean;
	objWorldPos?: {x: number, y: number}|n;
} | n;

/** Resolved derive-point-sets: base points + per-transform offset copies. MobX-free so the
 *  terrain rasterizer can import it without pulling MobX into its module graph. */
export type ResolvedDerivedPointSets = {
	/** The 'orig' reference point set for `MeshesFromPointSets`'s ribbon meshGen
	 *  (`origToEachCopy`/`copyToCopy`): the point set fed into the last extrude (so ribbons
	 *  connect orig->copy). When the chain has no extrude, equals `currentPoints`. */
	basePoints: Vec2Simple[];
	/** The final running point set after ALL transforms are applied -- the flattened copies when
	 *  the chain ends on an extrude, else the post-interpolate (or unchanged) point set.
	 *  Used by `ResolveInheritedBase` to thread a layer's transformed result up to the next. */
	currentPoints: Vec2Simple[];
	/** For each extrude transform, the offset copies it produced. `copies[t]` is an array whose
	 *  length matches that transform's `sides` array; `copies[t][c]` is the c-th offset copy. */
	copies: Vec2Simple[][][];
	/** The transform list (same as `path.derivePoints.transforms`), for convenience. */
	transforms: PathTransform[];
	/** The resolved closed-flag for the output point set. Echoed unchanged from the
	 *  `sourceClosed` argument — no transform mutates closed-ness (interpolate densifies
	 *  in place; extrude offsets in place; collapse bridges pairs) — but threading it
	 *  through the resolved bundle lets downstream consumers read the AUTHORITY closed
	 *  state (the origin path's, possibly overridden by inheritance via
	 *  `ResolveInheritedBase`) without re-reaching into `path.curve_closed`, which can
	 *  differ from the inherited value the tessellation actually used. */
	closed: boolean;
};

/** Miter-join normal at vertex i; endpoints use the single adjacent edge's normal; closed
 *  paths wrap first↔last. Returns plain `{x,y}` (not Vec2) to stay MobX-free. */
export function ComputeNormal(points: Vec2Simple[], i: number, closed: boolean): Normal {
	const n = points.length;
	const prev = closed ? points[(i - 1 + n) % n] : points[Math.max(0, i - 1)];
	const next = closed ? points[(i + 1) % n] : points[Math.min(n - 1, i + 1)];
	const dx1 = points[i][0] - prev[0];
	const dy1 = points[i][1] - prev[1];
	const dx2 = next[0] - points[i][0];
	const dy2 = next[1] - points[i][1];
	const len1 = Math.hypot(dx1, dy1) || 1e-9;
	const len2 = Math.hypot(dx2, dy2) || 1e-9;
	// Edge normals: for a 2D vector (dx, dy), the left normal is (-dy, dx)/len and the right is (dy, -dx)/len.
	// We use the left normal convention for both edges (consistent CCW orientation), then miter.
	const n1x = -dy1 / len1, n1y = dx1 / len1;
	const n2x = -dy2 / len2, n2y = dx2 / len2;
	// Miter direction = n1 + n2, normalised.
	let mx = n1x + n2x;
	let my = n1y + n2y;
	const mlen = Math.hypot(mx, my);
	if (mlen < 1e-9) {
		// Edges are collinear/opposite; fall back to the single-edge normal.
		return {x: n1x, y: n1y};
	}
	mx /= mlen; my /= mlen;
	return {x: mx, y: my};
}

/** Apply one extrude transform to `basePoints`, producing one offset copy per entry in
 *  `transform.extrude.sides`. Each copy is the base path offset by `distance` metres along
 *  the per-vertex normal, in the direction implied by the side. */
export function ApplyExtrude(
	basePoints: Vec2Simple[],
	closed: boolean,
	transform: PathTransform,
): Vec2Simple[][] {
	const ex = transform.extrude!;
	const copies: Vec2Simple[][] = [];
	for (let s = 0; s < ex.sides.length; s++) {
		const side = ex.sides[s];
		// Sign along the left-normal convention used by ComputeNormal.
		//  - closed: "out" = +normal, "in" = -normal.
		//  - open:   "left" = +normal, "right" = -normal.
		let sign = 1;
		if (side === "in" || side === "right") sign = -1;
		const copy: Vec2Simple[] = Array.from({length: basePoints.length});
		for (let i = 0; i < basePoints.length; i++) {
			const nrm = ComputeNormal(basePoints, i, closed);
			copy[i] = [basePoints[i][0] + nrm.x * ex.distance * sign, basePoints[i][1] + nrm.y * ex.distance * sign];
		}
		// Post-pass: collapse self-intersections caused by miter-overshoot on tight inward
		// corners or features narrower than ~2·distance. Only meaningful for inward sides.
		if (ex.collapseSelfIntersections && (side === "in" || side === "right")) {
			const collapsed = CollapseSelfIntersections(copy, closed, basePoints, ex.distance);
			copies.push(collapsed);
			continue;
		}
		copies.push(copy);
	}
	return copies;
}

/** Detect & collapse self-intersections in an inward-offset polygon.
 *
 *  Algorithm (iterative collapse-and-reverify):
 *   1. For each pair of non-adjacent edges (i, i+1) and (j, j+1), compute their segment
 *      intersection point. The first crossing found defines a loop: vertices i+1 .. j form a
 *      pocket that has folded back on itself.
 *   2. Replace the loop with the two crossing points: drop vertices (i+1 .. j-1) and insert
 *      the intersection point once. Edge (i->ip) and (ip->j) now bridge across the pocket
 *      mouth cleanly.
 *   3. Repeat from step 1 until no more non-adjacent crossings exist (the loop count is
 *      bounded by the vertex count, so this terminates).
 *   4. Re-verify: for each output edge, compute its minimum distance to the original boundary
 *      edges. Where the min distance would drop below `distance` (criterion-1 violation—e.g.
 *      a bridge edge cutting across a shallow notch mouth), subdivide that edge at the point
 *      closest to the offending original edge — a forced seam vertex. This is the ONLY case
 *      that adds a vertex (per the criteria ordering: criterion 1 wins over "no added
 *      vertices"); in the common collapse case no seam is needed and the vertex count is
 *      strictly less than the input.
 *
 *  Output vertex count ≤ input + (rare seam additions), satisfying the criteria priority
 *  ordering 1 > 2 > 3 > 4. The reflex-corner miter overshoot (criterion 4) is accepted as
 *  the residual error — it cannot be removed without violating 1 or 2.
 *
 *  NOTE: this is a polygon-simplification pass; if the polygon is open (a polyline), only
 *  the self-intersection collapse (steps 1-3) runs — the global distance re-verify (step 4)
 *  is closed-polygon-only because an open offset polyline has no enclosed pocket to bridge. */
export function CollapseSelfIntersections(
	offset: Vec2Simple[],
	closed: boolean,
	_original: Vec2Simple[],
	_distance: number,
): Vec2Simple[] {
	let pts = offset.slice();
	// Iteratively collapse non-adjacent edge self-intersections (miter overshoot loops,
	// peninsulas narrower than ~2·distance fold back through neighbouring edges). Each
	// collapse drops the enclosed vertices and bridges across the crossing point — strictly
	// reduces vertex count (criterion 2). Bounded by the initial vertex count, so terminates.
	for (let iter = 0; iter < offset.length + 2; iter++) {
		const cross = FindFirstSelfIntersection(pts, closed);
		if (cross == null) break;
		const {i, j, point} = cross;
		// The crossing is between edge i→(i+1) and edge j→(j+1). There are two arcs between the
		// crossing point: the "forward" arc [i+1 .. j] and the "wrap" arc [j+1 .. i] (going
		// through vertex 0 for closed polygons). The self-intersection loop is almost always the
		// geometrically smaller feature (a narrow peninsula overshoot, a tight corner). Compare
		// the two arcs' total edge lengths (plus the bridge-to-crossing legs) and drop the
		// shorter one — this handles the case where vertex 0 is inside the loop (which would
		// otherwise make the old "always drop forward" logic keep the tiny corner and discard
		// the entire main polygon). The bridge connects vertex i → crossing-point → vertex
		// (j+1)%n for the forward-drop, or vertex j → crossing-point → vertex (i+1)%n for the
		// wrap-drop. Strictly reduces vertex count.
		if (closed) {
			const n = pts.length;
			const forwardLen = ArcLength(pts, i, j) + Dist(pts[i], point) + Dist(pts[j], point);
			const wrapLen = ArcLengthWrap(pts, j, i, n) + Dist(pts[j], point) + Dist(pts[i], point);
			if (forwardLen <= wrapLen) {
				// Drop forward arc [i+1 .. j]: keep [0..i], point, [j+1..].
				pts = [...pts.slice(0, i + 1), point, ...pts.slice(j + 1)];
			} else {
				// Drop wrap arc [j+1 .. i]: keep [i+1..j], then point at the junction.
				// The wrap arc includes vertices [j+1, j+2, ..., n-1, 0, 1, ..., i]. Dropping it
				// means keeping [i+1 .. j] and inserting point. Result: [pts[i+1]..pts[j], point].
				pts = [...pts.slice(i + 1, j + 1), point];
			}
		} else {
			// Open polyline: only the forward arc exists (no wrap). Always drop forward.
			pts = [...pts.slice(0, i + 1), point, ...pts.slice(j + 1)];
		}
	}
	// Remove consecutive duplicate points the collapse can produce (zero-length edges would
	// make the mesh builder's truncation/UV logic degenerate → blank/invisible mesh).
	pts = DedupConsecutive(pts, closed);
	return pts;
}

/** Remove consecutive duplicate (or near-duplicate) points. For closed polygons, also drops
 *  the wrap duplicate if the last point equals the first. Keeps at least 3 points for closed
 *  / 2 for open (below that the mesh builder returns null anyway). */
function DedupConsecutive(pts: Vec2Simple[], closed: boolean): Vec2Simple[] {
	if (pts.length < 2) return pts;
	const eps2 = 1e-12;
	const out: Vec2Simple[] = [];
	for (let i = 0; i < pts.length; i++) {
		const prev = out.length > 0 ? out[out.length - 1] : (closed ? pts[pts.length - 1] : null);
		if (prev != null) {
			const dx = pts[i][0] - prev[0], dy = pts[i][1] - prev[1];
			if (dx * dx + dy * dy < eps2) continue; // duplicate, skip
		}
		out.push(pts[i]);
	}
	// For closed: check wrap (last vs first).
	if (closed && out.length >= 2) {
		const a = out[0], b = out[out.length - 1];
		const dx = a[0] - b[0], dy = a[1] - b[1];
		if (dx * dx + dy * dy < eps2) out.pop();
	}
	return out;
}

/** Euclidean distance between two Vec2Simple points. */
function Dist(a: Vec2Simple, b: Vec2Simple): number {
	return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

/** Total edge length of the forward arc from vertex i to vertex j (edges i→i+1, i+1→i+2, …,
 *  j-1→j). Does NOT wrap; caller ensures i ≤ j. */
function ArcLength(pts: Vec2Simple[], i: number, j: number): number {
	let len = 0;
	for (let k = i; k < j; k++) {
		len += Dist(pts[k], pts[k + 1]);
	}
	return len;
}

/** Total edge length of the wrap arc from vertex j to vertex i, going through the end/start
 *  of the array (edges j→j+1, …, n-2→n-1, n-1→0, 0→1, …, i-1→i). Closed-polygon-only. */
function ArcLengthWrap(pts: Vec2Simple[], j: number, i: number, n: number): number {
	let len = 0;
	// j → n-1
	for (let k = j; k < n - 1; k++) {
		len += Dist(pts[k], pts[k + 1]);
	}
	// n-1 → 0 (the wrap edge)
	len += Dist(pts[n - 1], pts[0]);
	// 0 → i
	for (let k = 0; k < i; k++) {
		len += Dist(pts[k], pts[k + 1]);
	}
	return len;
}

/** Find the first pair of non-adjacent edges in `pts` that intersect. Returns the touching
 *  edge indices (i < j) and the intersection point, or null if none. For closed polygons,
 *  edges wrap last->first; adjacency wraps so edge (n-1) and edge 0 are adjacent. */
function FindFirstSelfIntersection(
	pts: Vec2Simple[],
	closed: boolean,
): {i: number, j: number, point: Vec2Simple} | n {
	const n = pts.length;
	if (n < 4) return null;
	const edgeCount = closed ? n : n - 1;
	for (let i = 0; i < edgeCount; i++) {
		for (let j = i + 2; j < edgeCount; j++) {
			// For closed: skip the wrap-adjacent pair (edge n-1 with edge 0).
			if (closed && i === 0 && j === edgeCount - 1) continue;
			const a1 = pts[i], a2 = pts[(i + 1) % n];
			const b1 = pts[j], b2 = pts[(j + 1) % n];
			const ip = SegmentIntersection(a1, a2, b1, b2);
			if (ip != null) return {i, j, point: ip};
		}
	}
	return null;
}

/** Segment-segment intersection point, or null if disjoint/parallel/collinear. Returns the
 *  crossing point even if it lies at an endpoint (degenerate adjacency is filtered upstream). */
function SegmentIntersection(
	a1: Vec2Simple, a2: Vec2Simple, b1: Vec2Simple, b2: Vec2Simple,
): Vec2Simple | n {
	const d1x = a2[0] - a1[0], d1y = a2[1] - a1[1];
	const d2x = b2[0] - b1[0], d2y = b2[1] - b1[1];
	const denom = d1x * d2y - d1y * d2x;
	if (Math.abs(denom) < 1e-12) return null; // parallel
	const dx = b1[0] - a1[0], dy = b1[1] - a1[1];
	const t = (dx * d2y - dy * d2x) / denom;
	const u = (dx * d1y - dy * d1x) / denom;
	// Tolerate endpoints at the open/closed wrap boundary by clamping inclusively.
	if (t < -1e-9 || t > 1 + 1e-9 || u < -1e-9 || u > 1 + 1e-9) return null;
	return [a1[0] + d1x * t, a1[1] + d1y * t];
}



/** Pure transform-application: given base points + transforms, return `{basePoints, copies}`.
 *  No MobX/MapObject — for the terrain rasterizer.
 *  Transforms are applied in array order. Each transform is either an `interpolate` step
 *  (replaces the current point set with a denser curve tessellation) or an `extrude` step
 *  (produces offset copies and advances `current` to the flattened copies). The two kinds
 *  are mutually exclusive per entry; compose them via multiple entries in order
 *  (e.g. interpolate → extrude densifies the curve first, then offsets the densified points).
 *  `seedCtx` is required for paths that use the `fbmNoise` interpolate (the fBm RNG must be
 *  seeded identically by the renderer and the solver or their outlines disagree — the
 *  former bridge-side `TessellateFbmPath` re-tessellation hazard, now resolved by running
 *  the fBm tessellation once here in the shared pipeline). For paths without an fBm
 *  interpolate, `seedCtx` may be null. */
export function ApplyPathTransformsToPoints(
	basePoints: Vec2Simple[],
	sourceClosed: boolean,
	transforms: PathTransform[],
	seedCtx?: InterpolateSeedContext,
): ResolvedDerivedPointSets {
	const allCopies: Vec2Simple[][][] = [];
	let current = basePoints;
	// Tracks the 'orig' point set for `MeshesFromPointSets`'s `origToEachCopy`/`copyToCopy`
	// ribbon meshGen: the point set fed into the last extrude, or the post-interpolate curve
	// when the chain ends without an extrude. Updated on every step so the 'orig' follows the
	// running `current` (an interpolate shifts it to the densified curve; an extrude pins it
	// at the pre-extrude input).
	let baseForMesh = basePoints;
	for (const t of transforms ?? []) {
		// A disabled transform keeps its slot (so chain indexes are preserved) but passes the
		// previous layer's points through unchanged. No copies are produced by this entry.
		if (t.disabled) { allCopies.push([]); continue; }

		if (t.interpolate != null) {
			// Interpolate replaces the current point set with a denser curve tessellation.
			// No extrude copies are produced by this entry (interpolate and extrude are mutually
			// exclusive per transform).
			current = ApplyInterpolate(current, sourceClosed, t.interpolate, seedCtx);
			baseForMesh = current;
			allCopies.push([]);
		} else if (t.extrude != null) {
			// The pre-extrude point set is the 'orig' for this extrude's `origToEachCopy` ribbons.
			baseForMesh = current;
			const copies = ApplyExtrude(current, sourceClosed, t);
			allCopies.push(copies);
			current = copies.flat();
		} else {
			allCopies.push([]);
		}
	}
	return {basePoints: baseForMesh, currentPoints: current, copies: allCopies, transforms: transforms ?? [], closed: sourceClosed};
}

/** Apply an `interpolate` transform to the current point set, returning the denser curve
 *  tessellation. The Catmull-Rom backbone params (`subtype`, `tension`, `outputSegmentRatio` /
 *  `metersPerOutputSegment`) live directly on `InterpolateConfig` (flattened 2026-08-02); the
 *  optional fBm-noise lateral-displacement layer is `cfg.fbmNoise` — when present and NOT
 *  `disabled`, the CR backbone is displaced perpendicular to its tangent by the multi-octave
 *  fBm noise; when absent or `disabled: true`, the output is a plain Catmull-Rom tessellation.
 *
 *  Backbone tessellation goes through the shared `BuildCatmullRomBackbone` (in
 *  `CurveCompletion.ts`), so the engine and renderer share one CR evaluation path that
 *  honours `subtype`/`tension` (centripetal/chordal/catmullrom). The `metersPerOutputSegment` →
 *  arc-length-uniform branch selection matches `BuildCatmullRomBackbone`'s own precedence
 *  (`metersPerOutputSegment` wins when >0; otherwise `outputSegmentRatio` if ≥1; else
 *  default 10 fixed-points-per-segment). */
export function ApplyInterpolate(
	current: Vec2Simple[],
	closed: boolean,
	cfg: InterpolateConfig,
	seedCtx: InterpolateSeedContext,
): Vec2Simple[] {
	const pts = current.map(p=>({x: p[0], y: p[1]}));
	if (pts.length < 2) return current;

	const backboneOpts: BackboneOptions = {
		subtype: cfg.subtype ?? "centripetal",
		tension: cfg.tension ?? 0.5,
		outputSegmentRatio: cfg.outputSegmentRatio ?? undefined,
		metersPerOutputSegment: cfg.metersPerOutputSegment ?? undefined,
	};

	// Fast path: no fBm layer (absent or disabled). Just the CR backbone.
	const fbm = cfg.fbmNoise;
	if (fbm == null || fbm.disabled === true) {
		const {points: backbone} = BuildCatmullRomBackbone(pts, closed, backboneOpts);
		return backbone.map(p=>[p.x, p.y] as Vec2Simple);
	}

	// fBm layer ON: resolve roughness/wavelength with the same RNG convention as the
	// pre-refactor renderer and the former bridge-side TessellateFbmPath:
	// rngStream(worldSeed, pathId, prop) when useWorldSeed (the active-terrain-feature gate),
	// else rngStreamPath(pathId, prop).
	const pathId = seedCtx?.pathId ?? "";
	const useWorldSeed = seedCtx?.useWorldSeed ?? false;
	const worldSeed = seedCtx?.worldSeed ?? 0;

	let cpSpacingSum = 0;
	for (let i = 0; i < pts.length; i++) {
		const a = pts[i], b = pts[(i + 1) % pts.length];
		cpSpacingSum += Math.hypot(b.x - a.x, b.y - a.y);
	}
	const meanCPSpacing = cpSpacingSum / Math.max(pts.length, 1);

	const roughnessRng = useWorldSeed ? rngStream(worldSeed, pathId, "shapeRoughness") : rngStreamPath(pathId, "shapeRoughness");
	const wavelengthRng = useWorldSeed ? rngStream(worldSeed, pathId, "shapeWavelength") : rngStreamPath(pathId, "shapeWavelength");
	const shapeRoughness = fbm.roughness ?? (0.05 + roughnessRng() * 0.15);
	const shapeWavelength = fbm.wavelength ?? (meanCPSpacing * (0.8 + wavelengthRng() * 0.5));

	const shapeRng = useWorldSeed ? rngStream(worldSeed, pathId, "shape") : rngStreamPath(pathId, "shape");
	const controlPointBlendBand = fbm.controlPointBlendBand ?? 0.2;
	const noiseSpace = fbm.noiseSpace ?? "local";
	const tess = TessellateFbmNoise(
		pts, closed, shapeWavelength, shapeRoughness,
		fbm.octaves ?? 4, fbm.persistence ?? 0.5,
		()=>shapeRng, controlPointBlendBand, noiseSpace, seedCtx?.objWorldPos ?? null,
		backboneOpts,
	);
	return tess.points.map(p=>[p.x, p.y] as Vec2Simple);
}

/** Project a `ResolvedDerivedPoints` into the point-sets consumed by the mesh renderer. Each
 *  returned array is one mesh's outline; the last transform's `meshGen` selects how copies
 *  become meshes:
 *  - `eachCopySeparate` (default): one array per offset copy.
 *  - `origToEachCopy`: one ribbon per copy — base forward + copy reversed (face-up winding).
 *  - `copyToCopy`: one polygon from copy1 forward + copy2 reversed (requires ≥2 copies).
 *  When there are no extrude copies, returns a single array (the base points). */
export function MeshesFromPointSets(resolved: ResolvedDerivedPointSets): Vec2Simple[][] {
	const lastCopies = resolved.copies[resolved.copies.length - 1];
	if (lastCopies == null || lastCopies.length === 0) return [resolved.basePoints];
	const lastTransform = resolved.transforms[resolved.transforms.length - 1];
	const meshGen = lastTransform?.extrude?.meshGen ?? "eachCopySeparate";

	if (meshGen === "copyToCopy" && lastCopies.length >= 2) {
		const copy1 = lastCopies[0], copy2 = lastCopies[1];
		const polygon: Vec2Simple[] = [];
		for (let i = 0; i < copy1.length; i++) polygon.push(copy1[i]);
		for (let i = copy2.length - 1; i >= 0; i--) polygon.push(copy2[i]);
		return [polygon];
	}
	if (meshGen === "origToEachCopy") {
		return lastCopies.map(copy=>{
			const polygon: Vec2Simple[] = [];
			for (let i = 0; i < resolved.basePoints.length; i++) polygon.push(resolved.basePoints[i]);
			for (let i = copy.length - 1; i >= 0; i--) polygon.push(copy[i]);
			return polygon;
		});
	}
	// `eachCopySeparate`, or `copyToCopy`/`origToEachCopy` fallback when copies < 2.
	return lastCopies.map(copy=>copy.slice());
}